import { useState, useMemo, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Alert, AlertTitle, AlertDescription } from "../../ui/alert";
import { Info, AlertTriangle } from "lucide-react";
import {
  Plus,
  Trash2,
  FileText,
  ListChecks,
  RefreshCw,
  Settings,
  FileQuestion,
  Pencil,
  CheckCircle,
} from "lucide-react";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import {
  GradingType,
  testApi,
  TestDifficulty,
  TestQuestionType,
  TestType,
} from "@/api/testApi";
import { useNavigate } from "react-router-dom";
import { updateCourse } from "@/api/courseApi";
import { Loader2 } from "lucide-react";

function areObjectsEqual(obj1, obj2) {
  if (obj1 === obj2) return true;

  if (obj1 === null || obj2 === null) return obj1 === obj2;

  if (typeof obj1 !== "object" || typeof obj2 !== "object") {
    return obj1 === obj2;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (let key of keys1) {
    if (!keys2.includes(key)) return false;

    if (!areObjectsEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
}

// Simple QuestionEditor component for this implementation
const QuestionEditor = ({ isOpen, onClose, onSubmit, question, isEditing }) => {
  const [formData, setFormData] = useState({
    question: "",
    options: ["", "", "", ""],
    correctAnswer: 0,
    explanation: "",
  });

  useEffect(() => {
    if (question) {
      setFormData({
        question: question.question || "",
        options: question.options?.map((opt) =>
          typeof opt === "object" ? opt.text : opt
        ) || ["", "", "", ""],
        correctAnswer:
          typeof question.correctAnswer === "object"
            ? parseInt(question.correctAnswer.id)
            : question.correctAnswer || 0,
        explanation: question.explanation || "",
      });
    } else {
      setFormData({
        question: "",
        options: ["", "", "", ""],
        correctAnswer: 0,
        explanation: "",
      });
    }
  }, [question, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.question.trim()) {
      toast.error("Question text is required");
      return;
    }

    const filledOptions = formData.options.filter((opt) => opt.trim());
    if (filledOptions.length < 2) {
      toast.error("At least 2 options are required");
      return;
    }

    const questionData = {
      id: question?.id,
      question: formData.question,
      options: formData.options.map((text, index) => ({
        id: String(index),
        text: text,
      })),
      correctAnswer: {
        id: String(formData.correctAnswer),
        text: formData.options[formData.correctAnswer] || "",
      },
      explanation: formData.explanation,
      type: "multiple_choice",
    };

    onSubmit(questionData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">
          {isEditing ? "Edit Question" : "Add New Question"}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="question">Question *</Label>
            <textarea
              id="question"
              value={formData.question}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, question: e.target.value }))
              }
              className="w-full p-2 border rounded-md min-h-[100px]"
              placeholder="Enter your question"
              required
            />
          </div>

          <div>
            <Label>Options *</Label>
            {formData.options.map((option, index) => (
              <div key={index} className="flex items-center space-x-2 mt-2">
                <input
                  type="radio"
                  name="correctAnswer"
                  checked={formData.correctAnswer === index}
                  onChange={() =>
                    setFormData((prev) => ({ ...prev, correctAnswer: index }))
                  }
                />
                <Input
                  value={option}
                  onChange={(e) => {
                    const newOptions = [...formData.options];
                    newOptions[index] = e.target.value;
                    setFormData((prev) => ({ ...prev, options: newOptions }));
                  }}
                  placeholder={`Option ${index + 1}`}
                  className="flex-1"
                />
              </div>
            ))}
          </div>

          <div>
            <Label htmlFor="explanation">Explanation (Optional)</Label>
            <textarea
              id="explanation"
              value={formData.explanation}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  explanation: e.target.value,
                }))
              }
              className="w-full p-2 border rounded-md"
              placeholder="Explain why this is the correct answer"
              rows={3}
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              {isEditing ? "Update Question" : "Add Question"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Helper function to validate assessment set
const validateAssessmentSet = (set) => {
  const errors = [];

  if (!set.title?.trim()) {
    errors.push("Title is required");
  }

  if (!set.passingScore || set.passingScore < 0 || set.passingScore > 100) {
    errors.push("Passing score must be between 0-100%");
  }

  if (!set.maxAttempts || set.maxAttempts < 1) {
    errors.push("Maximum attempts must be at least 1");
  }

  if (!set.questionCount || set.questionCount < 1) {
    errors.push("Question count must be at least 1");
  }

  if (!set.questions || set.questions.length === 0) {
    errors.push("At least one question is required");
  }

  return errors;
};

// Helper function to get all test data from topics
const getTestsFromTopics = (topics = []) => {
  const assessmentData = {
    title: "Combined Assessment",
    type: TestType.ASSESSMENT,
    difficulty: TestDifficulty.INTERMEDIATE,
    showCorrectAnswers: true,
    allowRetakes: true,
    enableRandomization: true,
    passingScore: 75,
    maxAttempts: 3000,
    questionCount: 0,
    gradingType: GradingType.AUTOMATIC,
    description: "Assessment combining all quizzes",
    questionSets: [],
    questions: [],
  };

  const allQuizzes = [];

  topics.forEach((topic) => {
    if (!topic.learningMaterials) return;

    topic.learningMaterials.forEach((material) => {
      if (material.test && Array.isArray(material.test)) {
        allQuizzes.push(...material.test);
      }
    });
  });

  const maxSets = Math.max(
    ...allQuizzes.map((quiz) => quiz.questionSets?.length || 0),
    0
  );

  let mergedQuestionSets = [];
  let mergedQuestions = [];

  for (let i = 0; i < maxSets; i++) {
    let setQuestions = [];

    allQuizzes.forEach((quiz) => {
      const set = quiz.questionSets?.[i];
      if (set) {
        // take questions from each quiz's set
        if (Array.isArray(set.questions)) {
          setQuestions.push(...set.questions);
        }
      }
    });

    if (setQuestions.length > 0) {
      const newSet = {
        id: `assessment-set-${i + 1}`,
        title: `Assessment Set ${i + 1}`,
        questions: setQuestions,
      };

      mergedQuestionSets.push(newSet);
      mergedQuestions.push(...setQuestions);
    }
  }

  assessmentData.questionSets = mergedQuestionSets;
  assessmentData.questions = mergedQuestions;

  return [assessmentData];
};

// Helper function to create assessment sets from tests
const createAssessmentSetsFromTests = (tests) => {
  const assessmentSets = [];

  tests.forEach((test, testIndex) => {
    test.questionSets.forEach((questionSet, setIndex) => {
      const questions = questionSet.questions || [];

      const assessmentSet = {
        id: `aset-${Date.now()}-${testIndex}-${setIndex}`,
        title: `Assessment Set ${assessmentSets.length + 1}`,
        passingScore: questionSet.passingScore || 75,
        maxAttempts: questionSet.maxAttempts || 3,
        questionCount: questionSet.questionCount || questions.length,
        questions: questions.map((q) => ({
          ...q,
          assessmentSetId: questionSet.id,
          sourceQuestionId: q.id,
        })),
        sourceQuestions: [...questions],
        testId: test.id,
        questionSetId: questionSet.id,
        sourceMaterialId: test.sourceMaterialId,
        sourceTopicId: test.sourceTopicId,
      };

      assessmentSets.push(assessmentSet);
    });
  });

  return assessmentSets;
};

const AssessmentBuilderStep = ({
  setCurrentStep,
  mode = "ADD",
  onDataChange,
  initialData = null,
  topicsData = [],
}) => {
  const [selectedSetIndex, setSelectedSetIndex] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionEditorOpen, setQuestionEditorOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const tests = useMemo(() => {
    return getTestsFromTopics(topicsData || []);
  }, [topicsData]);

  const [assessmentConfig, setAssessmentConfig] = useState(() => {
    const savedAssessmentData = localStorage.getItem("assessment");
    if (savedAssessmentData) {
      try {
        const parsed = JSON.parse(savedAssessmentData);
        return {
          title: parsed.title || "Course Assessment",
          description: parsed.description || "Assessment for this course",
          passingScore: parsed.passingScore || 75,
          maxAttempts: parsed.maxAttempts || 3000,
          questionCount: parsed.questionsPerTest || 10,
          showCorrectAnswers: parsed.showCorrectAnswers ?? true,
          allowRetakes: parsed.allowRetakes ?? true,
          enableRandomization: parsed.enableRandomization ?? true,
        };
      } catch (error) {
        console.warn("Failed to parse saved assessment config:", error);
      }
    }

    return {
      title: `Assessment`,
      description: "Assessment for this course",
      passingScore: 75,
      maxAttempts: 3000,
      questionCount: 1,
      showCorrectAnswers: true,
      allowRetakes: true,
      enableRandomization: true,
    };
  });

  const [assessmentSets, setAssessmentSets] = useState(() => {
    if (initialData && initialData.length > 0) {
      return createAssessmentSetsFromTests(initialData);
    }

    // Try to load from localStorage if no initialData
    const savedAssessmentData = localStorage.getItem("assessment");
    if (savedAssessmentData) {
      try {
        const parsed = JSON.parse(savedAssessmentData);
        if (parsed.questionSets && Array.isArray(parsed.questionSets)) {
          console.log(parsed.questionSets);
          return parsed.questionSets;
        }
      } catch (error) {
        console.warn("Failed to parse saved assessment data:", error);
      }
    }

    if (tests.length > 0) {
      return createAssessmentSetsFromTests(tests);
    }

    return [];
  });

  console.log(assessmentSets);
  // Update assessment sets when topics data changes
  useEffect(() => {
    if (assessmentSets.length > 0) return;
    else if (tests.length > 0) {
      const newAssessmentSets = createAssessmentSetsFromTests(tests);
      setAssessmentSets(newAssessmentSets);
    }
  }, [tests, assessmentSets.length, createAssessmentSetsFromTests]);

  useEffect(() => {
    if (onDataChange) {
      onDataChange({
        assessmentSets,
        assessmentConfig, // Include assessment config
        tests,
        totalQuestions: assessmentSets.reduce(
          (total, set) => total + (set.questions?.length || 0),
          0
        ),
        validate: validateAndGetData,
      });
    }
  }, [assessmentSets, assessmentConfig, tests, onDataChange]);

  // Validate assessment sets
  const validateAssessmentSets = useCallback(() => {
    const errors = [];

    if (assessmentSets.length === 0) {
      errors.push("At least one assessment set is required");
      setValidationErrors(errors);
      return false;
    }

    let totalQuestions = 0;

    assessmentSets.forEach((set, index) => {
      const setErrors = validateAssessmentSet(set);
      errors.push(
        ...setErrors.map((error) => `Set "${set.title || index + 1}": ${error}`)
      );
      totalQuestions += set.questions?.length || 0;
    });

    if (totalQuestions === 0) {
      errors.push(
        "At least one question is required across all assessment sets"
      );
    }

    setValidationErrors(errors);
    return errors.length === 0;
  }, [assessmentSets]);

  // Function to trigger validation and show errors
  const validateAndShowErrors = useCallback(async () => {
    setShowValidationErrors(true);
    return validateAssessmentSets();
  }, [validateAssessmentSets]);

  // Method to validate and get current data
  const validateAndGetData = () => {
    return new Promise((resolve, reject) => {
      validateAndShowErrors().then((isValid) => {
        if (isValid) {
          resolve({
            assessmentSets,
            tests,
            totalQuestions: assessmentSets.reduce(
              (total, set) => total + (set.questions?.length || 0),
              0
            ),
          });
        } else {
          reject(validationErrors);
        }
      });
    });
  };

  // Expose validation method to parent
  useEffect(() => {
    if (onDataChange) {
      onDataChange({
        assessmentSets,
        tests,
        totalQuestions: assessmentSets.reduce(
          (total, set) => total + (set.questions?.length || 0),
          0
        ),
        validate: validateAndGetData,
      });
    }
  }, [assessmentSets, tests, onDataChange, validateAndGetData]);

  // Get the currently selected assessment set
  const selectedSet = useMemo(() => {
    return assessmentSets[selectedSetIndex] || null;
  }, [assessmentSets, selectedSetIndex]);

  // Handle saving a new or updated question
  const handleSaveQuestion = useCallback(
    (questionData) => {
      if (!selectedSet) {
        console.error("No selected set to add question to");
        toast.error("No assessment set selected");
        return;
      }

      try {
        const isEditing = !!questionData.id;

        if (!questionData.question?.trim()) {
          toast.error("Question text cannot be empty");
          return;
        }

        const questionToSave = {
          ...questionData,
          id:
            questionData.id ||
            `q-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: questionData.type || "multiple_choice",
          assessmentSetId: selectedSet.id,
          sourceMaterialId: selectedSet.sourceMaterialId,
          sourceTopicId: selectedSet.sourceTopicId,
          createdAt: questionData.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          options: questionData.options || ["", "", "", ""],
          correctAnswer: questionData.correctAnswer ?? 0,
          difficulty: questionData.difficulty || "INTERMEDIATE",
          weight: questionData.weight || 1,
          tags: Array.isArray(questionData.tags) ? [...questionData.tags] : [],
        };

        const updatedSets = assessmentSets.map((set) => {
          if (set.id === selectedSet.id) {
            const currentQuestions = Array.isArray(set.questions)
              ? [...set.questions]
              : [];
            let updatedQuestions;

            if (isEditing) {
              const questionIndex = currentQuestions.findIndex(
                (q) => q?.id === questionToSave.id
              );
              if (questionIndex === -1) {
                updatedQuestions = [...currentQuestions, questionToSave];
              } else {
                updatedQuestions = [...currentQuestions];
                updatedQuestions[questionIndex] = questionToSave;
              }
            } else {
              updatedQuestions = [...currentQuestions, questionToSave];
            }

            return {
              ...set,
              questions: updatedQuestions,
              questionCount: updatedQuestions.length,
              updatedAt: new Date().toISOString(),
            };
          }
          return set;
        });

        setAssessmentSets(updatedSets);
        setQuestionEditorOpen(false);
        setCurrentQuestion(null);

        toast.success(
          `Question ${isEditing ? "updated" : "added"} successfully`
        );
      } catch (error) {
        console.error("Error saving question:", error);
        toast.error(
          `Failed to save question: ${error.message || "Unknown error"}`
        );
      }
    },
    [selectedSet, assessmentSets]
  );

  // Handle editing an existing question
  const handleEditQuestion = useCallback((question) => {
    setCurrentQuestion(question);
    setQuestionEditorOpen(true);
  }, []);

  // Handle deleting a question
  const handleDeleteQuestion = useCallback(
    (questionId) => {
      if (!selectedSet) return;

      const updatedSets = assessmentSets.map((set) => {
        if (set.id === selectedSet.id) {
          const updatedQuestions = (set.questions || []).filter(
            (q) => q.id !== questionId
          );
          return {
            ...set,
            questions: updatedQuestions,
            questionCount: updatedQuestions.length,
            updatedAt: new Date().toISOString(),
          };
        }
        return set;
      });

      setAssessmentSets(updatedSets);
      toast.success("Question deleted successfully");
    },
    [selectedSet, assessmentSets]
  );

  // Handle adding a new question
  const handleAddQuestion = useCallback(() => {
    setCurrentQuestion(null);
    setQuestionEditorOpen(true);
  }, []);

  // Handle removing an assessment set
  const removeAssessmentSet = (setId) => {
    const updatedSets = assessmentSets.filter((set) => set.id !== setId);
    setAssessmentSets(updatedSets);

    if (selectedSetIndex >= updatedSets.length && updatedSets.length > 0) {
      setSelectedSetIndex(updatedSets.length - 1);
    } else if (updatedSets.length === 0) {
      setSelectedSetIndex(0);
    }

    toast.success("Assessment set removed");
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      const existingAssessment = JSON.parse(
        localStorage.getItem("assessment") || "null"
      );
      let existingAssessmentData;

      if (Array.isArray(existingAssessment)) {
        existingAssessmentData = existingAssessment[0];
      } else {
        existingAssessmentData = existingAssessment;
      }

      const courseId = sessionStorage.getItem("courseId");
      if (!courseId) {
        throw new Error("Course ID not found");
      }

      // CREATE NEW ASSESSMENT
      if (!existingAssessmentData || !existingAssessmentData.id) {
        console.log("Creating new assessment...");
        const totalQuestions = assessmentSets.reduce(
          (total, set) => total + (set.questions?.length || 0),
          0
        );
        const assessmentCreationData = {
          title: assessmentConfig.title,
          description: assessmentConfig.description,
          type: TestType.ASSESSMENT,
          difficulty: TestDifficulty.INTERMEDIATE,
          passingScore: assessmentConfig.passingScore,
          maxAttempts: assessmentConfig.maxAttempts,
          showCorrectAnswers: assessmentConfig.showCorrectAnswers,
          allowRetakes: assessmentConfig.allowRetakes,
          enableRandomization: assessmentConfig.enableRandomization,
          questionsPerTest: Math.min(
            assessmentConfig.questionCount,
            totalQuestions
          ),
          gradingType: GradingType.AUTOMATIC,
          courseId,
        };

        const assessmentCreation = await testApi.createTest(
          assessmentCreationData
        );

        if (!assessmentCreation.success) {
          throw new Error("Failed to create assessment");
        }

        const assessmentId = assessmentCreation.data.id;
        const createdAssessmentData = {
          ...assessmentCreation.data,
          id: assessmentId,
          questionSets: [],
        };

        // Create question sets and questions
        for (const [setIndex, set] of assessmentSets.entries()) {
          const questionSetCreationData = {
            name: set.title || `Assessment Set ${setIndex + 1}`,
            testId: assessmentId,
            weight: 1,
          };

          const questionSetCreation = await testApi.createQuestionSet(
            questionSetCreationData
          );

          if (!questionSetCreation.success) {
            console.error(`Failed to create question set ${setIndex + 1}`);
            continue;
          }

          const questionSetId = questionSetCreation.data.id;
          const createdQuestions = [];

          // Create questions for this set
          for (const questionData of set.questions || []) {
            const questionCreationData = {
              body: [
                {
                  type: TestQuestionType.SINGLE_SELECT,
                  question: questionData.question,
                  options: questionData.options || [],
                  correctAnswer: questionData.correctAnswer,
                  explanation: questionData.explanation || "",
                  tags: Array.isArray(questionData.tags)
                    ? questionData.tags
                    : [],
                  difficulty: TestDifficulty.INTERMEDIATE,
                  points: 1,
                },
              ],
              testId: assessmentId,
              questionSetId,
            };

            const questionCreation =
              await testApi.createQuestion(questionCreationData);

            if (questionCreation.success) {
              createdQuestions.push({
                ...questionData,
                id: questionCreation.data.id,
              });
            }
          }

          // Add the created question set to assessment data
          createdAssessmentData.questionSets.push({
            ...set,
            id: questionSetId,
            questions: createdQuestions,
          });
        }

        // Save to localStorage
        localStorage.setItem(
          "assessment",
          JSON.stringify(createdAssessmentData)
        );

        console.log("Assessment created successfully");
        return { success: true, data: createdAssessmentData };
      }

      // UPDATE EXISTING ASSESSMENT
      else {
        console.log("Updating existing assessment...");
        const totalQuestions = assessmentSets.reduce(
          (total, set) => total + (set.questions?.length || 0),
          0
        );
        const existingAssessmentConfig = {
          title: existingAssessmentData.title,
          description: existingAssessmentData.description,
          type: existingAssessmentData.type,
          difficulty: existingAssessmentData.difficulty,
          passingScore: existingAssessmentData.passingScore,
          maxAttempts: existingAssessmentData.maxAttempts,
          showCorrectAnswers: existingAssessmentData.showCorrectAnswers,
          allowRetakes: existingAssessmentData.allowRetakes,
          enableRandomization: existingAssessmentData.enableRandomization,
          questionsPerTest: existingAssessmentData.questionsPerTest,
          gradingType: existingAssessmentData.gradingType,
        };

        const currentAssessmentConfig = {
          title: assessmentConfig.title,
          description: assessmentConfig.description,
          type: TestType.ASSESSMENT,
          difficulty: TestDifficulty.INTERMEDIATE,
          passingScore: assessmentConfig.passingScore,
          maxAttempts: assessmentConfig.maxAttempts,
          showCorrectAnswers: assessmentConfig.showCorrectAnswers,
          allowRetakes: assessmentConfig.allowRetakes,
          enableRandomization: assessmentConfig.enableRandomization,
          questionsPerTest: Math.min(
            assessmentConfig.questionCount,
            totalQuestions
          ),
          gradingType: GradingType.AUTOMATIC,
        };

        // Update main assessment if config has changed
        const isAssessmentConfigSame = areObjectsEqual(
          existingAssessmentConfig,
          currentAssessmentConfig
        );

        if (!isAssessmentConfigSame) {
          console.log("Updating assessment metadata...");
          const updateResult = await testApi.updateTest(
            existingAssessmentData.id,
            currentAssessmentConfig
          );
          if (!updateResult.success) {
            console.error("Failed to update assessment metadata");
          }
        }

        const updatedAssessmentData = {
          ...existingAssessmentData,
          ...currentAssessmentConfig,
          questionSets: [...(existingAssessmentData.questionSets || [])],
        };

        const existingQuestionSets = updatedAssessmentData.questionSets || [];

        // Process assessment sets with update-first approach
        for (let setIndex = 0; setIndex < assessmentSets.length; setIndex++) {
          const currentSet = assessmentSets[setIndex];
          const existingQuestionSet = existingQuestionSets[setIndex];

          let questionSetId;
          let questionSetToUpdate;

          if (existingQuestionSet) {
            // UPDATE EXISTING QUESTION SET
            console.log(
              `Updating existing question set ${setIndex + 1}: ${currentSet.title}`
            );

            questionSetId = existingQuestionSet.id;

            // Check if question set metadata needs updating
            const questionSetUpdateData = {
              name: currentSet.title || `Assessment Set ${setIndex + 1}`,
              weight: 1,
            };

            const existingQuestionSetData = {
              name: existingQuestionSet.title || existingQuestionSet.name,
              weight: existingQuestionSet.weight || 1,
            };

            const isQuestionSetSame = areObjectsEqual(
              existingQuestionSetData,
              questionSetUpdateData
            );

            if (!isQuestionSetSame) {
              console.log(
                `Updating question set metadata for set ${setIndex + 1}`
              );
              try {
                await testApi.updateQuestionSet(
                  questionSetId,
                  questionSetUpdateData
                );
              } catch (error) {
                console.error(
                  `Failed to update question set ${questionSetId}:`,
                  error
                );
              }
            }

            // Update the question set in our local data
            questionSetToUpdate = {
              ...existingQuestionSet,
              ...currentSet,
              id: questionSetId,
              title: currentSet.title || `Assessment Set ${setIndex + 1}`,
              questions: [...(existingQuestionSet.questions || [])],
            };

            updatedAssessmentData.questionSets[setIndex] = questionSetToUpdate;
          } else {
            // CREATE NEW QUESTION SET (when number of sets increased)
            console.log(
              `Creating new question set ${setIndex + 1}: ${currentSet.title}`
            );

            const questionSetCreationData = {
              name: currentSet.title || `Assessment Set ${setIndex + 1}`,
              testId: existingAssessmentData.id,
              weight: 1,
            };

            const questionSetCreation = await testApi.createQuestionSet(
              questionSetCreationData
            );

            if (!questionSetCreation.success) {
              console.error(`Failed to create question set ${setIndex + 1}`);
              continue;
            }

            questionSetId = questionSetCreation.data.id;

            questionSetToUpdate = {
              ...currentSet,
              id: questionSetId,
              questions: [],
            };

            // Add new question set to our data
            updatedAssessmentData.questionSets.push(questionSetToUpdate);
          }

          // HANDLE QUESTIONS IN THIS SET
          const currentQuestions = currentSet.questions || [];
          const existingQuestions = questionSetToUpdate.questions || [];

          // Create a map of existing questions by ID for efficient lookup
          const existingQuestionsMap = new Map();
          existingQuestions.forEach((q) => {
            if (q.id) existingQuestionsMap.set(q.id, q);
          });

          const updatedQuestions = [];

          // Process each current question
          for (const questionData of currentQuestions) {
            const questionPayload = {
              type: TestQuestionType.SINGLE_SELECT,
              question: questionData.question,
              options: questionData.options || [],
              correctAnswer: questionData.correctAnswer,
              explanation: questionData.explanation || "",
              tags: Array.isArray(questionData.tags) ? questionData.tags : [],
              difficulty: TestDifficulty.INTERMEDIATE,
              points: 1,
            };

            if (questionData.id && existingQuestionsMap.has(questionData.id)) {
              // UPDATE EXISTING QUESTION
              const existingQuestion = existingQuestionsMap.get(
                questionData.id
              );

              const existingQuestionPayload = {
                type: existingQuestion.type,
                question: existingQuestion.question,
                options: existingQuestion.options,
                correctAnswer: existingQuestion.correctAnswer,
                explanation: existingQuestion.explanation,
                tags: existingQuestion.tags,
                difficulty: existingQuestion.difficulty,
                points: existingQuestion.points,
              };

              const isQuestionSame = areObjectsEqual(
                existingQuestionPayload,
                questionPayload
              );

              if (!isQuestionSame) {
                console.log(
                  `Updating question: ${questionData.question.substring(0, 50)}...`
                );
                try {
                  await testApi.updateQuestion(
                    questionData.id,
                    questionPayload
                  );
                } catch (error) {
                  console.error(
                    `Failed to update question ${questionData.id}:`,
                    error
                  );
                }
              }

              updatedQuestions.push({
                ...questionData,
                ...questionPayload,
              });

              // Remove from map so we know it's been processed
              existingQuestionsMap.delete(questionData.id);
            } else {
              // CREATE NEW QUESTION
              console.log(`Creating new question in set ${currentSet.title}`);

              const questionCreationData = {
                body: [questionPayload],
                testId: existingAssessmentData.id,
                questionSetId,
              };

              try {
                const questionCreation =
                  await testApi.createQuestion(questionCreationData);

                if (questionCreation.success) {
                  updatedQuestions.push({
                    ...questionData,
                    id: questionCreation.data.id,
                    ...questionPayload,
                  });
                }
              } catch (error) {
                console.error(`Failed to create question:`, error);
              }
            }
          }

          // Handle remaining questions (ones that were removed from the current set)
          for (const [questionId, removedQuestion] of existingQuestionsMap) {
            console.log(
              `Question removed from set: ${removedQuestion.question.substring(0, 50)}...`
            );
            try {
              await testApi.deleteQuestion(questionId);
            } catch (error) {
              console.error(`Failed to delete question ${questionId}:`, error);
              // If deletion fails, we might want to keep the question or handle it differently
            }
          }

          // Update questions in the question set
          questionSetToUpdate.questions = updatedQuestions;
        }

        // Note: We're not deleting extra question sets as per your requirement
        // If you had more question sets before, they will remain but won't be used
        if (existingQuestionSets.length > assessmentSets.length) {
          console.log(
            `Warning: ${existingQuestionSets.length - assessmentSets.length} question sets will remain unused`
          );
        }

        // Save updated data to localStorage
        localStorage.setItem(
          "assessment",
          JSON.stringify(updatedAssessmentData)
        );

        console.log("Assessment updated successfully");
        return { success: true, data: updatedAssessmentData };
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      toast.error(`Failed to save assessment: ${error.message}`);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Handle adding a new assessment set
  const addAssessmentSet = () => {
    const newSet = {
      id: `aset-${Date.now()}`,
      title: `Assessment Set ${assessmentSets.length + 1}`,
      passingScore: 75,
      questionCount: 0,
      maxAttempts: 3000,
      questions: [],
      sourceQuestions: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    const updatedSets = [...assessmentSets, newSet];
    setAssessmentSets(updatedSets);
    setSelectedSetIndex(updatedSets.length - 1);
  };

  // Memoize the questions list to prevent unnecessary re-renders
  const questionsList = useMemo(() => {
    return selectedSet?.questions || [];
  }, [selectedSet]);

  const disableAll = loading || mode === "VIEW";

  // Run validation whenever assessment sets change
  useEffect(() => {
    validateAssessmentSets();
  }, [assessmentSets, validateAssessmentSets]);
  return (
    <div className="space-y-6">
      <QuestionEditor
        isOpen={questionEditorOpen}
        onClose={() => setQuestionEditorOpen(false)}
        onSubmit={handleSaveQuestion}
        question={currentQuestion}
        isEditing={!!currentQuestion?.id}
      />

      {/* Validation Errors */}
      {showValidationErrors && validationErrors.length > 0 && (
        <Alert className="border-red-200 bg-red-50">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertTitle className="text-red-800">
            Assessment Validation Issues
          </AlertTitle>
          <AlertDescription className="text-red-700 mt-2">
            <ul className="list-disc pl-4 space-y-1">
              {validationErrors.map((error, index) => (
                <li key={index} className="text-sm">
                  {error}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        <h3 className="text-lg font-medium">Assessment Configuration</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-2">
            <Label htmlFor="passingScore">Passing Score (%) *</Label>
            <div className="relative">
              <Input
                id="passingScore"
                type="text"
                inputMode="numeric"
                disabled={disableAll}
                value={assessmentConfig.passingScore?.toString() || "75"}
                onChange={(e) => {
                  if (e.target.value === "" || /^\d*$/.test(e.target.value)) {
                    const numValue = parseInt(e.target.value, 10);
                    if (
                      e.target.value === "" ||
                      (numValue >= 0 && numValue <= 100)
                    ) {
                      setAssessmentConfig((prev) => ({
                        ...prev,
                        passingScore: e.target.value === "" ? 0 : numValue,
                      }));
                    }
                  }
                }}
                className={`pr-12 ${!assessmentConfig.passingScore || assessmentConfig.passingScore < 0 || assessmentConfig.passingScore > 100 ? "border-red-300" : ""}`}
                placeholder="75"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                %
              </span>
            </div>
            {(!assessmentConfig.passingScore ||
              assessmentConfig.passingScore < 0 ||
              assessmentConfig.passingScore > 100) && (
              <p className="text-sm text-red-600">
                Passing score must be between 0-100%
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Minimum score required to pass this assessment
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="maxAttempts">Maximum Attempts *</Label>
            <Input
              id="maxAttempts"
              type="number"
              min="0"
              disabled={disableAll}
              value={assessmentConfig.maxAttempts || "3000"}
              onChange={(e) => {
                const attempts = parseInt(e.target.value, 10);
                if (attempts >= 0) {
                  setAssessmentConfig((prev) => ({
                    ...prev,
                    maxAttempts: attempts,
                  }));
                }
              }}
              className={
                !assessmentConfig.maxAttempts ||
                assessmentConfig.maxAttempts < 0
                  ? "border-red-300"
                  : ""
              }
              placeholder="3000"
            />
            {(!assessmentConfig.maxAttempts ||
              assessmentConfig.maxAttempts < 0) && (
              <p className="text-sm text-red-600">
                Minimum attempts must be at least 0
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Number of attempts allowed for this assessment
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="questionCount">Questions Per Test *</Label>
            <Input
              id="questionCount"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              disabled={disableAll}
              value={assessmentConfig.questionCount || ""}
              onChange={(e) => {
                const inputValue = e.target.value;
                const parsedValue = parseInt(inputValue, 10);
                const totalAvailableQuestions = assessmentSets.reduce(
                  (total, set) => total + (set.questions?.length || 0),
                  0
                );

                if (inputValue === "" || isNaN(parsedValue)) {
                  setAssessmentConfig((prev) => ({
                    ...prev,
                    questionCount: "",
                  }));
                  return;
                }

                if (
                  totalAvailableQuestions > 0 &&
                  parsedValue > totalAvailableQuestions
                ) {
                  toast.error(
                    `Cannot exceed ${totalAvailableQuestions} available questions`
                  );
                  setAssessmentConfig((prev) => ({
                    ...prev,
                    questionCount: totalAvailableQuestions,
                  }));
                  return;
                }

                const newValue = Math.max(1, parsedValue);
                setAssessmentConfig((prev) => ({
                  ...prev,
                  questionCount: newValue,
                }));
              }}
              onBlur={(e) => {
                const parsedValue = parseInt(e.target.value, 10);
                const totalAvailableQuestions = assessmentSets.reduce(
                  (total, set) => total + (set.questions?.length || 0),
                  0
                );

                if (isNaN(parsedValue) || parsedValue < 1) {
                  setAssessmentConfig((prev) => ({
                    ...prev,
                    questionCount: 1,
                  }));
                } else if (
                  totalAvailableQuestions > 0 &&
                  parsedValue > totalAvailableQuestions
                ) {
                  setAssessmentConfig((prev) => ({
                    ...prev,
                    questionCount: totalAvailableQuestions,
                  }));
                }
              }}
              className={
                !assessmentConfig.questionCount ||
                assessmentConfig.questionCount < 1
                  ? "border-red-300"
                  : ""
              }
              placeholder="10"
            />
            {(!assessmentConfig.questionCount ||
              assessmentConfig.questionCount < 1) && (
              <p className="text-sm text-red-600">
                Question count must be at least 1
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              {assessmentSets.reduce(
                (total, set) => total + (set.questions?.length || 0),
                0
              ) > 0
                ? `Questions to include in each test (max: ${assessmentSets.reduce((total, set) => total + (set.questions?.length || 0), 0)} available)`
                : "Number of questions to include in each test"}
            </p>
          </div>
        </div>
      </div>
      <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left column - Assessment Sets */}
        <div className="space-y-4">
          <div className="flex items-center flex-wrap justify-between">
            <h3 className="text-lg font-medium">Assessment Sets</h3>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-3 bg-[var(--primary-font)] text-white hover:bg-[var(--secondary-font)] transition-all"
                disabled={disableAll}
                onClick={() => {
                  const freshTests = getTestsFromTopics(topicsData || []);
                  if (freshTests.length > 0) {
                    const newAssessmentSets =
                      createAssessmentSetsFromTests(freshTests);
                    if (newAssessmentSets.length > 0) {
                      setAssessmentSets(newAssessmentSets);
                      setSelectedSetIndex(0);
                      toast.success(
                        `Refreshed with ${newAssessmentSets.length} assessment sets from quizzes`
                      );
                    } else {
                      toast.warning(
                        "No valid assessment sets could be created from current quiz data"
                      );
                    }
                  } else {
                    toast.warning(
                      "No quiz data found in topics to refresh from"
                    );
                  }
                }}
              >
                <RefreshCw className="h-4 w-4" />
                Fetch quizzes Data
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={disableAll}
                onClick={addAssessmentSet}
              >
                <Plus className="h-4 w-4 mr-2" />
                New Set
              </Button>
            </div>
          </div>

          {assessmentSets.length > 0 ? (
            <div className="space-y-2">
              {assessmentSets.map((set, index) => {
                const setErrors = validateAssessmentSet(set);
                const hasErrors = setErrors.length > 0;

                return (
                  <Card
                    key={set.id}
                    className={`cursor-pointer transition-all hover:shadow-md py-0 ${
                      selectedSetIndex === index
                        ? "border-[1.5px] !border-[var(--primary-font)]"
                        : ""
                    } ${hasErrors ? "border-red-300 bg-red-50" : ""}`}
                    onClick={() => setSelectedSetIndex(index)}
                  >
                    <CardHeader
                      className={`p-4 ${
                        selectedSetIndex === index
                          ? "text-[var(--primary-font)]"
                          : ""
                      } ${hasErrors ? "text-red-700" : ""}`}
                    >
                      <div className="flex items-start justify-between space-x-3">
                        <div className="space-y-1">
                          <CardTitle className="text-base flex flex-wrap items-center gap-2">
                            {set.title}
                            {hasErrors && (
                              <AlertTriangle className="h-4 w-4 text-red-500" />
                            )}
                            {set.testId && (
                              <Badge variant="secondary" className="text-xs">
                                From Quiz
                              </Badge>
                            )}
                          </CardTitle>
                          <div className="flex flex-wrap gap-2">
                            <Badge variant="outline" className="text-xs">
                              {set.questions?.length || 0} Questions
                            </Badge>
                            {set.passingScore && (
                              <Badge variant="outline" className="text-xs">
                                {set.passingScore}% Pass
                              </Badge>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={disableAll}
                          className={`h-8 w-8 ${
                            selectedSetIndex === index
                              ? "bg-[var(--secondary-font)] text-white"
                              : ""
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            removeAssessmentSet(set.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="border-dashed border-red-300 bg-red-50">
              <CardContent className="p-8 text-center">
                <ListChecks className="h-12 w-12 mx-auto text-red-400 mb-4" />
                <h3 className="text-lg font-medium text-red-700 mb-2">
                  No assessment sets
                </h3>
                <p className="text-sm text-red-600 mb-4">
                  At least one assessment set is required to proceed
                </p>
                <Button
                  variant="outline"
                  disabled={disableAll}
                  onClick={addAssessmentSet}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Assessment Set
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right column - Selected Assessment */}
        <div className="md:col-span-2 min-h-[32rem]">
          {selectedSet ? (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl flex items-center gap-2">
                    {selectedSet.title}
                    {selectedSet.testId && (
                      <Badge variant="secondary" className="text-xs">
                        From Quiz
                      </Badge>
                    )}
                  </CardTitle>
                </div>
              </CardHeader>

              <Tabs defaultValue="questions" className="w-full">
                <div className="flex items-center justify-between px-4">
                  <TabsList className="flex items-center gap-4">
                    <TabsTrigger value="questions">
                      <FileQuestion className="h-4 w-4" />
                      Questions
                      {selectedSet.questions?.length === 0 && (
                        <AlertTriangle className="h-3 w-3 ml-1 text-red-500" />
                      )}
                    </TabsTrigger>
                  </TabsList>
                  <Button
                    onClick={handleAddQuestion}
                    className="w-fit"
                    disabled={disableAll}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Question
                  </Button>
                </div>

                <TabsContent value="questions" className="p-4 min-h-[32rem]">
                  {questionsList.length === 0 ? (
                    <div className="min-h-[30rem] flex flex-col items-center justify-center text-center p-8 border-2 border-dashed border-red-300 rounded-lg bg-red-50">
                      <FileText className="h-12 w-12 text-red-400 mb-4" />
                      <h3 className="text-lg font-medium text-red-700">
                        No questions yet
                      </h3>
                      <p className="text-sm text-red-600 mt-1 mb-4">
                        At least one question is required in each assessment set
                      </p>
                      <Button onClick={handleAddQuestion} disabled={disableAll}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add First Question
                      </Button>
                    </div>
                  ) : (
                    <div className="w-full space-y-4 max-h-[30rem] min-h-[30rem] overflow-auto">
                      <Accordion type="multiple" className="space-y-2">
                        {questionsList.map((question, index) => {
                          const questionErrors = [];
                          if (!question.question?.trim())
                            questionErrors.push("Missing question text");
                          if (!question.options || question.options.length < 2)
                            questionErrors.push("Need at least 2 options");
                          if (
                            question.correctAnswer === undefined ||
                            question.correctAnswer === null
                          )
                            questionErrors.push("No correct answer selected");

                          const hasQuestionErrors = questionErrors.length > 0;

                          return (
                            <AccordionItem
                              key={question.id}
                              value={`question-${question.id}`}
                              className={`border rounded-lg overflow-hidden ${hasQuestionErrors ? "border-red-300 bg-red-50" : ""}`}
                            >
                              <div
                                className={`flex items-center justify-between px-4 py-3 ${hasQuestionErrors ? "bg-red-100" : "bg-muted/50"}`}
                              >
                                <AccordionTrigger className="flex-1 items-center py-0 hover:no-underline [&[data-state=open]>svg]:rotate-180">
                                  <div className="flex items-center space-x-3">
                                    <div
                                      className={`h-8 w-8 rounded-md flex items-center justify-center text-white font-bold flex-shrink-0 ${
                                        hasQuestionErrors
                                          ? "bg-red-500"
                                          : "bg-[var(--primary-font)]"
                                      }`}
                                    >
                                      {hasQuestionErrors ? (
                                        <AlertTriangle className="h-4 w-4" />
                                      ) : (
                                        index + 1
                                      )}
                                    </div>

                                    <div className="flex-1 flex items-center justify-between min-w-0">
                                      <span
                                        className={`text-left font-bold  line-clamp-1 ${
                                          hasQuestionErrors
                                            ? "text-red-700"
                                            : ""
                                        }`}
                                      >
                                        {question.question ||
                                          "Untitled Question"}
                                      </span>

                                      {hasQuestionErrors && (
                                        <Badge
                                          variant="destructive"
                                          className="text-xs flex-shrink-0 ml-2"
                                        >
                                          {questionErrors.length} issues
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                </AccordionTrigger>
                                <div className="flex items-center space-x-1 ml-2">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    disabled={disableAll}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditQuestion(question);
                                    }}
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    disabled={disableAll}
                                    className="h-8 w-8 text-destructive hover:text-destructive"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteQuestion(question.id);
                                    }}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </div>
                              <AccordionContent className="px-4 pb-4 pt-2">
                                <div className="space-y-4">
                                  {hasQuestionErrors && (
                                    <div className="bg-red-100 border border-red-300 rounded p-3">
                                      <h4 className="text-sm font-medium text-red-800 mb-1">
                                        Issues with this question:
                                      </h4>
                                      <ul className="text-sm text-red-700 list-disc pl-4">
                                        {questionErrors.map((error, i) => (
                                          <li key={i}>{error}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}

                                  <div>
                                    <h4 className="text-sm font-medium mb-2">
                                      Options
                                    </h4>
                                    <div className="space-y-2">
                                      {question.options?.map((option, i) => {
                                        const isCorrect =
                                          question.correctAnswer?.id ===
                                            i.toString() ||
                                          question.correctAnswer === i;
                                        return (
                                          <div
                                            key={i}
                                            className={`flex items-center space-x-2 p-2 rounded ${
                                              isCorrect
                                                ? "bg-[#D3F8DD] text-[#47864D] border-none"
                                                : "bg-muted/50"
                                            }`}
                                          >
                                            <div
                                              className={`h-4 w-4 rounded-full flex items-center justify-center ${
                                                isCorrect
                                                  ? "bg-[#D3F8DD] text-[#47864D] border-none"
                                                  : "border border-border"
                                              }`}
                                            >
                                              {isCorrect && (
                                                <CheckCircle className="h-3 w-3" />
                                              )}
                                            </div>
                                            <span className="text-sm">
                                              {typeof option === "object"
                                                ? option.text
                                                : option}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>

                                  {question.explanation && (
                                    <div className="bg-[#D9ECF8] text-[#2B6A7F] rounded-md p-4">
                                      <h4 className="text-sm font-bold mb-1">
                                        Explanation
                                      </h4>
                                      <p className="text-sm ml-2">
                                        {question.explanation}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          );
                        })}
                      </Accordion>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </Card>
          ) : (
            <Card className="border-dashed border-red-300 bg-red-50 grid place-items-center min-h-[32rem]">
              <CardContent className="p-8 text-center">
                <FileText className="h-12 w-12 mx-auto text-red-400 mb-4" />
                <h3 className="text-lg font-medium text-red-700 mb-2">
                  No assessment selected
                </h3>
                <p className="text-sm text-red-600 mt-1">
                  Select an assessment from the list or create a new one
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {mode !== "VIEW" && (
        <div className="flex justify-between mt-8">
          <Button
            variant="outline"
            onClick={() => setCurrentStep((prev) => prev - 1)}
          >
            Previous
          </Button>
          <div className="flex gap-2 items-center">
            <Button
              type="button"
              variant="outline"
              disabled={loading}
              onClick={async () => {
                setLoading(true);
                try {
                  const savingData = await handleSubmit();
                  if (savingData.success) {
                    toast.success("Course Saved successfully");
                    navigate("/admin/manage-courses");
                  } else {
                    toast.error("Failed to save course");
                  }
                } catch (error) {
                  console.error("Error saving assessment data:", error);
                }
                setLoading(false);
              }}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving
                </>
              ) : (
                "Save as Draft"
              )}
            </Button>

            <Button
              type="button"
              variant="outline"
              disabled={loading}
              onClick={async () => {
                setLoading(true);
                try {
                  const savingData = await handleSubmit();
                  if (savingData.success) {
                    const courseId = sessionStorage.getItem("courseId");
                    await updateCourse(courseId, {
                      isActive: true,
                    });
                    toast.success("Course activated successfully");
                    navigate("/admin/manage-courses");
                  } else {
                    toast.error("Failed to activate course");
                  }
                } catch (error) {
                  console.error("Error saving assessment data:", error);
                }
                setLoading(false);
              }}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving
                </>
              ) : (
                "Submit & Publish"
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentBuilderStep;
